import { SquareArrowOutUpRight } from "lucide-react";
import React from "react";
import { Link, useLoaderData } from "react-router-dom";
import { getToken } from "../../../../../utils/token";
import instance, { facUrl } from "../../../../../axios/instance";
import { toast } from "react-toastify";
import { Suspense } from "react";
import { Calendar } from "../../../../../components/Calendar";

const today = new Date();
const year = today.getFullYear();
const month = today.getMonth(); // 0-based

const dayOfWeekNames = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const getDateForDay = (targetDayName) => {
  const targetDayIndex = dayOfWeekNames.indexOf(targetDayName.toLowerCase());
  const currentDayIndex = today.getDay(); // 0 is Sunday, 6 is Saturday
  const diff = (targetDayIndex - currentDayIndex + 7) % 7; // Days to add to get to the target day
  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + diff);
  return targetDate;
};

const convertDocsAvailabilityToEvent = (doctors) => {
  const availableDays = [];
  doctors.forEach((doctor) => {
    for (let i in doctor.availability) {
      if (doctor.availability[i]) {
        availableDays.push({ [i]: doctor.availability[i], doctor });
      }
    }
  });
  const events = availableDays.flatMap((dayObject) => {
    const dayName = Object.keys(dayObject)[0];
    const times = dayObject[dayName];

    const targetDate = getDateForDay(dayName);

    return times.map(({ time_id, time }) => {
      const [hour, minute, period] = time
        .match(/(\d+):(\d+)\s(AM|PM)/)
        .slice(1);
      const startHour =
        period === "PM" && hour !== "12" ? parseInt(hour) + 12 : parseInt(hour);
      const startMinute = parseInt(minute);

      return {
        id: `${dayName}-${time_id}`,
        title: `${dayObject.doctor.prefix} ${dayObject.doctor.lastname} ${dayObject.doctor.firstname}`,
        start: new Date(
          year,
          month,
          targetDate.getDate(),
          startHour,
          startMinute
        ),
      };
    });
  });

  return events;
};

export const treatmentProvidersLoader = async ({ params }) => {
  const token = getToken();
  const res = await instance.get("/doctor/facility", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const licensedStates = await instance.get(
    `/doctor/facility/licensed-states`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const doctors = await instance.get(`/doctor/facility/doctors-availability`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const doctorsAvailability = convertDocsAvailabilityToEvent(doctors.data);

  return {
    facility: res.data,
    licensedStates: licensedStates.data.sort(
      (a, b) => b.docs.length - a.docs.length
    ),
    doctorsAvailability,
  };
};

export const TreatmentProvidersPage = () => {
  const { facility, licensedStates, doctorsAvailability } = useLoaderData();

  function handleCopy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Website link copied");
  }

  const link = facUrl(facility);

  return (
    <div className="px-7 py-5 w-full">
      <div
        className={`${
          licensedStates.length >= 1 ? "grid grid-cols-[300px_auto]" : ""
        } gap-x-5 items-start`}
      >
        {licensedStates.length >= 1 && (
          <div>
            <div>
              <h1 className="font-medium text-[17px] mb-4">Licensed States:</h1>
              <div className="bg-white border rounded-md overflow-hidden grid grid-cols-1 max-h-[70vh] overflow-y-scroll scrollbar-hide">
                {licensedStates.map((license, index) => (
                  <div
                    key={index}
                    className={`flex justify-between items-center p-4 border-b ${
                      index % 2 === 0 ? "bg-[#f9f9f9]" : ""
                    }`}
                  >
                    <h2 className="font-semibold text-gray-800">
                      {license.state}
                    </h2>
                    <div className="flex items-baseline gap-x-2">
                      <span className="text-2xl font-bold text-primary">
                        {license.docs.length}
                      </span>
                      <span className="text-xs text-gray-500">doctors</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div>
          <div className="font-medium text-[17px] mb-4">
            Your facility's unique website URL:
          </div>
          <div className="grid grid-cols-[auto_100px_100px] gap-2">
            <input
              className="bg-gray-200 rounded-[3px] p-3 text-gray-500 text-[14px]"
              disabled
              value={link}
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleCopy(link)}
                className="px-6 border border-primary bg-primary text-white text-[14px] font-medium rounded-md"
              >
                Copy
              </button>
              <Link
                to={link}
                target="_blank"
                className="px-6 flex items-center gap-2 border border-primary bg-white text-primary text-[14px] font-medium rounded-md"
              >
                Open <SquareArrowOutUpRight />
              </Link>
            </div>
          </div>
          <div className="mt-10">
            <Suspense fallback={<div>Loading calendar...</div>}>
              <Calendar
                events={doctorsAvailability}
                handleEventClick={() => {}}
                title={"Doctor's availability"}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};
